// Destructible battlefield: trees, bushes, rocks, boulders and rock spires + blast craters.
// Pure, deterministic (own seeded Rng so it never perturbs combat randomness). No three/DOM.
import { Rng } from './util';
import type { HillSpec, PropSpec, TerrainSpec } from './mapdef';

export type PropKind = 'tree' | 'rock' | 'boulder' | 'spire' | 'bush';
export const PROP_KINDS: PropKind[] = ['tree', 'rock', 'boulder', 'spire', 'bush'];

/** Variant shape data (owner 2026-11-02: 'several various versions of the trees… tall short, skinny, wider, add some bushes
 *  too'). Neutral numbers only: r = hit radius at s = 1 (m; trees: the visible trunk base, bushes: the leafy mass), s / h =
 *  ranges for the overall scale and the height factor, hp = health multiplier, w = pick weight. The renderer builds one shape
 *  per variant (render/trees.ts) whose trunk / canopy match these radii. */
export interface VariantDef { name: string; r: number; s: [number, number]; h: [number, number]; hp: number; w: number }
export const TREE_VARIANTS: VariantDef[] = [
  { name: 'classic', r: 0.7, s: [0.9, 1.3], h: [0.92, 1.1], hp: 1, w: 3 },     // owner-picked painted canopy (design E)
  { name: 'poplar', r: 0.55, s: [0.95, 1.25], h: [1.0, 1.18], hp: 0.9, w: 2 },  // tall + skinny column
  { name: 'round', r: 0.85, s: [0.85, 1.15], h: [0.85, 1.0], hp: 1.15, w: 2 },  // short, wide dome
  { name: 'broad', r: 0.8, s: [1.0, 1.25], h: [0.95, 1.1], hp: 1.3, w: 2 },     // tall trunk, broad spreading crown
  { name: 'sapling', r: 0.38, s: [0.75, 1.0], h: [0.9, 1.15], hp: 0.55, w: 2 }, // young, thin, sparse
  { name: 'tiered', r: 0.65, s: [0.9, 1.2], h: [0.95, 1.15], hp: 1, w: 2 },     // layered pads
];
export const BUSH_VARIANTS: VariantDef[] = [
  { name: 'dome', r: 1.15, s: [0.8, 1.2], h: [0.9, 1.1], hp: 1, w: 3 },         // round leafy mound (~1.4 m tall)
  { name: 'spread', r: 1.5, s: [0.8, 1.15], h: [0.85, 1.05], hp: 1.2, w: 2 },   // wide, low, flowering (~1.1 m)
  { name: 'upright', r: 0.85, s: [0.85, 1.2], h: [0.9, 1.15], hp: 0.8, w: 2 },  // tall narrow clump (~2.2 m)
];
const pickVariant = (rng: Rng, defs: VariantDef[]) => {
  let t = rng.next() * defs.reduce((a, d) => a + d.w, 0);
  for (let i = 0; i < defs.length; i++) { t -= defs[i].w; if (t < 0) return i; }
  return defs.length - 1;
};
/** trees and bushes: they burn, shed leaves and are drawn per variant */
export const isFlora = (k: PropKind): boolean => k === 'tree' || k === 'bush';

export interface Prop {
  alive: boolean; kind: PropKind; x: number; z: number;
  /** collision / hit radius */ r: number;
  /** visual scale + yaw (render only uses these) */ s: number; h: number; rot: number;
  hp: number; maxHp: number;
  /** shape variant (trees / bushes: index into TREE_VARIANTS / BUSH_VARIANTS; 0 for rocks) */
  v: number;
  /** seconds of hit-shake left (render) */ shake: number;
  solid: boolean;
  /** MAP-FIRE: seconds this prop has been burning (0 = not burning); char 0..1 = how blackened it is (render tint) */
  burn: number; char: number;
}
/** What damaged / broke a prop (render hint for the destruction style, mirrors KILL_SRC for foes; never feeds back into
 *  gameplay). blast = radial explosion, beam = Nova Lance, cut = Scythe Disc, pierce = Finger Beam needle, crush = boss
 *  trample, wall = Riftline, vortex = Singularity, chip = small hits (bolts, barrage pops, rush), burn = map fire. */
export const PROP_SRC = { other: 0, blast: 1, beam: 2, cut: 3, pierce: 4, crush: 5, wall: 6, vortex: 7, chip: 8, burn: 9 } as const;
export interface Crater { x: number; z: number; r: number; born: number; /** depth multiplier (Zenith digs deeper) */ deep?: number }
/** Burned grass (beam trails, blast rims): fades embers, the char stays. */
export interface Scorch { x: number; z: number; r: number; born: number; rot: number }
export const MAX_SCORCH = 420;

const KIND: Record<PropKind, { hp: number; solid: boolean }> = {
  tree: { hp: 45, solid: false },
  rock: { hp: 70, solid: false },
  boulder: { hp: 320, solid: true },
  spire: { hp: 1400, solid: true },
  bush: { hp: 20, solid: false },
};

export const MAX_CRATERS = 72;
/** Anything that accepts terrain events (the Sim's event array). */
export interface EventSink { push(e: TerrainEvent): unknown }
const CELL = 8;

export type TerrainEvent =
  | { t: 'propHit'; i: number; src: number }
  /** ox/oz = origin of the damage (blast centre, beam / wall / projectile start), power = overkill-scaled fling speed,
   *  src = PROP_SRC, dx/dz = unit travel direction of a beam / needle / disc / wall / projectile (0,0 for radial) */
  | { t: 'propBreak'; i: number; ox: number; oz: number; power: number; src: number; dx: number; dz: number }
  | { t: 'crater'; x: number; z: number; r: number };

/** largest crater radius (m): arena radius 160 + 1.7 * 9 = 175.3 < deformable ground edge 179 (render/ground.ts GROUND_HALF) */
export const MAX_CRATER_R = 9;
/** The original arena layout (map #1, Canyon Meadow): rolling hills, 9 spires, 24 boulders, 80 trees, 50 rocks and 60
 *  bushes at the 72 m density. Other maps pass their own TerrainSpec (src/maps/<id>/core.ts); this stays the default so
 *  `new Terrain(seed, R)` keeps generating exactly the same layout for the same seed. */
export const CLASSIC_HILLS: HillSpec = { mounds: 15, dip: 0.27, rad: [14, 30], dipRad: [10, 18], amp: [0.9, 2.7], dipAmp: [0.45, 1.0], swell: 0.45, max: 3.0, min: -1.2 };
export const CLASSIC_TERRAIN: TerrainSpec = {
  hills: CLASSIC_HILLS,
  props: [
    { kind: 'spire', count: 9, rMin: 26, inset: 6, size: [3.4, 5.4], clear: 10, h: [10, 18], hpPer: 4.5 },
    { kind: 'boulder', count: 24, rMin: 12, inset: 3, size: [1.5, 2.5], clear: 4, h: [0.8, 1.3], hpPer: 2 },
    { kind: 'tree', count: 80, rMin: 10, inset: 2, size: [0.7, 0.7], clear: 1.4, h: [1, 1], variants: TREE_VARIANTS },
    { kind: 'rock', count: 50, rMin: 8, inset: 2, size: [0.5, 0.9], clear: 1, h: [0.8, 1.2] },
    { kind: 'bush', count: 60, rMin: 9, inset: 2, size: [1, 1], clear: 0, h: [1, 1], variants: BUSH_VARIANTS, layout: 'clumps' },
  ],
};
/** How many of each prop the arena places. Shared with the renderer so its instance buffers always fit (owner: some rocks
 *  were invisible — the arena grew to 110 m but the renderer still had room for the 72 m counts, so extras were solid but unseen). */
/** EDGE WALLS (owner 2026-11-02): how far (m) a prop's drawn shape reaches from its centre toward the wall (canopies, the
 *  wider-than-footprint rock meshes, leaning spires). Props are kept at least this far inside the arena radius. */
export function wallReach(p: Pick<Prop, 'kind' | 'r'>): number {
  switch (p.kind) {
    case 'tree': return 3.6;
    case 'bush': return p.r + 0.9;
    case 'rock': return p.r * 1.4 + 0.5;
    case 'boulder': return p.r * 1.3 + 0.7;
    case 'spire': return p.r * 1.25 + 1.5;
  }
}

export function propCounts(arenaR: number, spec: TerrainSpec = CLASSIC_TERRAIN): Record<PropKind, number> {
  const k = (arenaR / 72) ** 2; // keep the original 72 m density when the arena grows
  const out: Record<PropKind, number> = { spire: 0, boulder: 0, tree: 0, rock: 0, bush: 0 };
  for (const p of spec.props) out[p.kind] += Math.round(p.count * k);
  return out;
}

/** Perf budget for a map's total props (Docs/DESIGN/MAPS.md): 600 at the 110 m arena, scaled with the area so density stays
 *  the same (~1270 at 160 m). The renderer only draws the chunks near the camera (render/chunkCull.ts). */
export const propBudget = (arenaR: number): number => Math.round(600 * (arenaR / 110) ** 2);

/** Gentle rolling hills (owner 2026-11-02: 'add various gradual elevations to the map so it's not totally flat, add some hills
 *  with little to medium elevation, slight inclines and declines'). Pure + seeded: a sum of broad Gaussian mounds and a few
 *  shallow dips plus a long low swell, flat at the spawn and blended to flat before the arena wall. Gameplay stays 2D: sim
 *  heights (hover, projectile y) are measured above the local ground; the renderer draws the ground and everything on it
 *  from this one function. */
export const HILL_MAX = 3.0, HILL_MIN = -1.2;
/** HillSpec.mounds is a count at the 110 m arena (when hills were added); larger arenas place proportionally more */
export const HILL_REF_R = 110;
export const hillAreaK = (arenaR: number): number => Math.max(1, (arenaR / HILL_REF_R) ** 2);
export class Hills {
  private d: Float32Array; private n: number; private ph: [number, number, number, number];
  private swell: number; private hmax: number; private hmin: number;
  /** spec null = flat ground */
  constructor(seed: number, readonly arenaR: number, spec: HillSpec | null = CLASSIC_HILLS) {
    const rng = new Rng((seed * 48271 + 7) >>> 0), S = spec ?? CLASSIC_HILLS;
    // mound counts were authored on the 110 m arena (owner 2026-11-02): keep the same hill density as the arena grows
    this.n = spec ? Math.round(S.mounds * hillAreaK(arenaR)) : 0; this.d = new Float32Array(this.n * 4);
    this.swell = spec ? S.swell : 0; this.hmax = S.max; this.hmin = S.min;
    for (let k = 0; k < this.n; k++) {
      const a = rng.range(0, Math.PI * 2), dist = Math.sqrt(rng.range((16 / arenaR) ** 2, ((arenaR - 14) / arenaR) ** 2)) * arenaR;
      const dip = rng.next() < S.dip;
      this.d.set([Math.cos(a) * dist, Math.sin(a) * dist, rng.range(dip ? S.dipRad[0] : S.rad[0], dip ? S.dipRad[1] : S.rad[1]), dip ? -rng.range(S.dipAmp[0], S.dipAmp[1]) : rng.range(S.amp[0], S.amp[1])], k * 4);
    }
    this.ph = [rng.range(0, 6.283), rng.range(0, 6.283), rng.range(0.016, 0.024), rng.range(0.014, 0.022)];
  }
  /** ground height (m) at (x, z) */
  height(x: number, z: number): number {
    if (!this.n && !this.swell) return 0;
    const r = Math.hypot(x, z), R = this.arenaR;
    const m = smooth(9, 24, r) * (1 - smooth(R - 16, R - 4, r));
    if (m <= 0) return 0;
    let h = this.swell * Math.sin(x * this.ph[2] + this.ph[0]) * Math.sin(z * this.ph[3] + this.ph[1]);
    const d = this.d;
    for (let k = 0; k < this.n; k++) {
      const i = k * 4, dx = x - d[i], dz = z - d[i + 1], rr = d[i + 2];
      const q = (dx * dx + dz * dz) / (rr * rr); if (q < 9) h += d[i + 3] * Math.exp(-q);
    }
    // soft limits: overlapping mounds round off instead of stacking into a peak
    h = h > 0 ? this.hmax * Math.tanh(h / this.hmax) : -this.hmin * Math.tanh(h / -this.hmin);
    return h * m;
  }
}
const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

export class Terrain {
  props: Prop[] = [];
  /** base ground elevation (render draws the ground from it; gameplay stays 2D) */
  readonly hills: Hills;
  craters: Crater[] = [];
  scorches: Scorch[] = [];
  private scorchNext = 0;
  private cells = new Map<number, number[]>();
  private half: number;

  /** edgeWall (default on): props whose drawn shape would reach into the arena's edge wall are slid inward (wallReach), so
   *  nothing clips the wall. Off reproduces the raw pre-wall generator (tests pin it). No rng is consumed either way. */
  constructor(seed: number, readonly arenaR: number, spec: TerrainSpec = CLASSIC_TERRAIN, edgeWall = true) {
    this.half = Math.ceil((arenaR + 20) / CELL);
    this.hills = new Hills(seed, arenaR, spec.hills);
    const rng = new Rng((seed * 7919 + 17) >>> 0);
    const k = (arenaR / 72) ** 2;
    const place = (ps: PropSpec, n: number) => {
      const kind = ps.kind, vs = ps.variants, rMin = ps.rMin, rMax = arenaR - ps.inset;
      for (let c = 0, tries = 0; c < n && tries < n * 40; tries++) {
        const a = rng.range(0, Math.PI * 2), d = Math.sqrt(rng.range((rMin / arenaR) ** 2, (rMax / arenaR) ** 2)) * arenaR;
        const x = Math.cos(a) * d, z = Math.sin(a) * d;
        // variant kinds (trees): the variant sets the trunk radius, scale range and height factor
        const v = vs ? pickVariant(rng, vs) : 0, vd = vs?.[v];
        const s0 = vd ? rng.range(vd.s[0], vd.s[1]) : 0;
        const r = vd ? vd.r * s0 : rng.range(ps.size[0], ps.size[1]);
        if (this.props.some((p) => (p.x - x) ** 2 + (p.z - z) ** 2 < (p.r + r + ps.clear) ** 2)) continue;
        const def = KIND[kind];
        const s = vd ? s0 : r;
        const h = vd ? rng.range(vd.h[0], vd.h[1]) : rng.range(ps.h[0], ps.h[1]);
        const hp = def.hp * (vd ? vd.hp : ps.hpPer ? r / ps.hpPer : 1);
        this.props.push({ alive: true, kind, x, z, r, s, h, rot: rng.range(0, Math.PI * 2), hp, maxHp: hp, v, shake: 0, solid: def.solid, burn: 0, char: 0 });
        c++;
      }
    };
    for (const ps of spec.props) if ((ps.layout ?? 'scatter') === 'scatter') place(ps, Math.round(ps.count * k));
    spec.props.filter((ps) => ps.layout === 'clumps').forEach((ps, i) => this.placeClumps(seed, ps, Math.round(ps.count * k), i));
    if (edgeWall) for (const p of this.props) {
      const d = Math.hypot(p.x, p.z), lim = arenaR - wallReach(p);
      if (d > lim && d > 1e-6) { p.x *= lim / d; p.z *= lim / d; }
    }
    this.props.forEach((p, i) => this.index(p, i, true));
  }

  /** Clumped props (bushes): small groups, about half of them hugging a tree's canopy edge, the rest gathered in the open.
   *  Own seeded stream, placed after everything else, so they never disturb the rest of the layout. */
  private placeClumps(seed: number, ps: PropSpec, n: number, idx: number): void {
    const rng = new Rng((seed * 6151 + 29 + idx * 7919) >>> 0), R = this.arenaR, trees = this.props.filter((p) => p.kind === 'tree');
    const kind = ps.kind, vs = ps.variants ?? BUSH_VARIANTS, rMin = ps.rMin, rMax = R - ps.inset;
    let placed = 0;
    for (let tries = 0; placed < n && tries < n * 30; tries++) {
      let cx: number, cz: number;
      if (trees.length && rng.next() < 0.55) {
        const t = trees[Math.floor(rng.next() * trees.length)], a = rng.range(0, Math.PI * 2), d = rng.range(2.2, 4.6) * t.s;
        cx = t.x + Math.cos(a) * d; cz = t.z + Math.sin(a) * d;
      } else {
        const a = rng.range(0, Math.PI * 2), d = Math.sqrt(rng.range((10 / R) ** 2, ((R - 3) / R) ** 2)) * R;
        cx = Math.cos(a) * d; cz = Math.sin(a) * d;
      }
      const group = 1 + Math.floor(rng.next() * 4); // 1–4 per clump
      for (let g = 0; g < group && placed < n; g++) {
        const a = rng.range(0, Math.PI * 2), d = g === 0 ? 0 : rng.range(1.1, 2.4);
        const x = cx + Math.cos(a) * d, z = cz + Math.sin(a) * d, dd = Math.hypot(x, z);
        if (dd < rMin || dd > rMax) continue;
        const v = pickVariant(rng, vs), vd = vs[v], s = rng.range(vd.s[0], vd.s[1]), r = vd.r * s;
        const h = rng.range(vd.h[0], vd.h[1]), rot = rng.range(0, Math.PI * 2);
        // clump members may touch each other, but keep clear of trunks and rocks
        if (this.props.some((p) => (p.x - x) ** 2 + (p.z - z) ** 2 < (p.r + r * (p.kind === kind ? 0.55 : 1) + (p.kind === kind ? 0 : 0.35)) ** 2)) continue;
        const hp = KIND[kind].hp * vd.hp;
        this.props.push({ alive: true, kind, x, z, r, s, h, rot, hp, maxHp: hp, v, shake: 0, solid: KIND[kind].solid, burn: 0, char: 0 });
        placed++;
      }
    }
  }

  private index(p: Prop, i: number, add: boolean): void {
    const c0 = this.cell(p.x - p.r), c1 = this.cell(p.x + p.r), r0 = this.cell(p.z - p.r), r1 = this.cell(p.z + p.r);
    for (let cx = c0; cx <= c1; cx++) for (let cz = r0; cz <= r1; cz++) {
      const key = cx * 4096 + cz; let arr = this.cells.get(key); if (!arr) this.cells.set(key, (arr = []));
      if (add) arr.push(i); else { const k = arr.indexOf(i); if (k >= 0) arr.splice(k, 1); }
    }
  }

  /** QA staging only (?propdestroycheck / ?firecheck): move a prop (and its spatial index), restored to full health. */
  moveProp(i: number, x: number, z: number): void {
    const p = this.props[i]; this.index(p, i, false);
    p.x = x; p.z = z; p.alive = true; p.hp = p.maxHp; p.shake = 0; p.burn = 0; p.char = 0;
    this.index(p, i, true);
  }

  private cell(v: number): number { return Math.floor(v / CELL) + this.half; }

  /** Visit live props whose cell overlaps the circle (may repeat a prop at most once per cell; callers are idempotent). */
  private near(x: number, z: number, r: number, fn: (p: Prop, i: number) => void): void {
    const seen = this.seen; seen.length = 0;
    for (let cx = this.cell(x - r); cx <= this.cell(x + r); cx++) for (let cz = this.cell(z - r); cz <= this.cell(z + r); cz++) {
      const arr = this.cells.get(cx * 4096 + cz); if (!arr) continue;
      for (const i of arr) { const p = this.props[i]; if (!p.alive || seen.includes(i)) continue; seen.push(i); fn(p, i); }
    }
  }
  private seen: number[] = [];

  /** Push a circle out of solid props (player + ordinary foes). */
  collide(o: { x: number; z: number }, r: number): void {
    this.near(o.x, o.z, r + 6, (p) => {
      if (!p.solid) return;
      const dx = o.x - p.x, dz = o.z - p.z, d = Math.hypot(dx, dz), min = p.r + r;
      if (d >= min) return;
      if (d < 1e-4) { o.x += min; return; }
      o.x = p.x + (dx / d) * min; o.z = p.z + (dz / d) * min;
    });
  }

  /** Damage one prop. src = PROP_SRC (render hint only), (dx, dz) = travel direction of the cause (0,0 = radial). */
  hit(p: Prop, i: number, dmg: number, out: EventSink, ox: number, oz: number, src: number = PROP_SRC.other, dx = 0, dz = 0): void {
    if (!p.alive) return;
    const over = -(p.hp - dmg);
    p.hp -= dmg;
    if (p.hp <= 0) { p.alive = false; out.push({ t: 'propBreak', i, ox, oz, power: Math.min(22, 7 + Math.sqrt(Math.max(0, over)) * 0.6), src, dx, dz }); return; }
    if (p.shake <= 0.05) out.push({ t: 'propHit', i, src });
    p.shake = 0.25;
  }

  /** Area damage (explosions, pulses, the boss trampling). */
  damageCircle(x: number, z: number, r: number, dmg: number, out: EventSink, src: number = PROP_SRC.blast): void {
    this.near(x, z, r + 6, (p, i) => { if ((p.x - x) ** 2 + (p.z - z) ** 2 <= (r + p.r) ** 2) this.hit(p, i, dmg, out, x, z, src); });
  }

  /** Beam damage along a segment of half-width w. (px, pz) = the direction the props are pushed / cut along (default:
   *  along the line); Riftline passes its travel direction, which is across its wall line. */
  damageLine(x: number, z: number, dx: number, dz: number, len: number, w: number, dmg: number, out: EventSink, src: number = PROP_SRC.beam, px = dx, pz = dz): void {
    this.near(x + dx * len / 2, z + dz * len / 2, len / 2 + w + 6, (p, i) => {
      const rx = p.x - x, rz = p.z - z, along = rx * dx + rz * dz;
      if (along < -p.r || along > len + p.r) return;
      if (Math.abs(rx * -dz + rz * dx) > w + p.r) return;
      this.hit(p, i, dmg, out, x, z, src, px, pz);
    });
  }

  /** A projectile passing through (x, z) with radius r: damages each prop it overlaps ONCE per projectile (`seen` holds
   *  the prop indices this projectile already hit; the caller owns and resets it). Never blocks the projectile. */
  damageSweep(x: number, z: number, r: number, dmg: number, out: EventSink, src: number, dx: number, dz: number, seen: number[]): void {
    for (let cx = this.cell(x - r - 6); cx <= this.cell(x + r + 6); cx++) for (let cz = this.cell(z - r - 6); cz <= this.cell(z + r + 6); cz++) {
      const arr = this.cells.get(cx * 4096 + cz); if (!arr) continue;
      for (let k = 0; k < arr.length; k++) {
        const i = arr[k], p = this.props[i];
        if (!p.alive || (p.x - x) ** 2 + (p.z - z) ** 2 > (r + p.r) ** 2 || seen.includes(i)) continue;
        seen.push(i);
        this.hit(p, i, dmg, out, x - dx * 2, z - dz * 2, src, dx, dz);
      }
    }
  }

  /** Spatial lookup without allocation for MAP-FIRE (props whose footprint touches the circle). */
  forNear(x: number, z: number, r: number, fn: (p: Prop, i: number) => void): void { this.near(x, z, r, fn); }

  addCrater(x: number, z: number, r: number, tick: number, out: EventSink, deep = 1): void {
    if (r < 0.8) return;
    r = Math.min(r, MAX_CRATER_R); // keeps every crater (reach 1.7r) inside the deformable ground, even at the arena wall
    // merge into an overlapping crater of similar size instead of stacking dozens of decals
    for (const c of this.craters) {
      if ((c.x - x) ** 2 + (c.z - z) ** 2 < (c.r * 0.45) ** 2 && c.r >= r * 0.8) { c.r = Math.min(MAX_CRATER_R, c.r * 1.06 + 0.05, Math.max(c.r, r) * 1.6); c.born = tick; if (deep > (c.deep ?? 1)) c.deep = deep; out.push({ t: 'crater', x: c.x, z: c.z, r: c.r }); return; }
    }
    this.addScorch(x, z, r * 1.7, tick);
    if (this.craters.length >= MAX_CRATERS) this.craters.shift();
    this.craters.push(deep !== 1 ? { x, z, r, born: tick, deep } : { x, z, r, born: tick });
    out.push({ t: 'crater', x, z, r });
  }

  /** Burn the ground. Nearby marks refresh instead of stacking; a ring buffer bounds the count. */
  addScorch(x: number, z: number, r: number, tick: number): void {
    for (const sc of this.scorches) {
      if ((sc.x - x) ** 2 + (sc.z - z) ** 2 < (Math.min(sc.r, r) * 0.6) ** 2) { sc.born = tick; if (r > sc.r) sc.r = Math.min(r, sc.r * 1.15); return; }
    }
    const mark = { x, z, r, born: tick, rot: ((x * 12.9898 + z * 78.233) % 6.283) };
    if (this.scorches.length < MAX_SCORCH) this.scorches.push(mark);
    else { this.scorches[this.scorchNext] = mark; this.scorchNext = (this.scorchNext + 1) % MAX_SCORCH; }
  }

  update(dt: number): void { for (const p of this.props) if (p.shake > 0) p.shake = Math.max(0, p.shake - dt); }
}
